import { buildPageHeader } from "./sheet-ui.js";
/**
 * voice-v8 — Automatik-View (2026-07-04, Martin — Redesign).
 *
 * Volle Sichtbarkeit + Kontrolle ueber alle automatischen Prozesse:
 *  - deine geplanten Auftraege: bearbeiten (Zeit/Rhythmus/Titel) + abbrechen,
 *    mit Status-Punkt (gruen = letzter Lauf erfolgreich), naechstem Lauf,
 *    Rhythmus als Chips und letztem Ergebnis.
 *  - System-Automatik: jeder Job mit Zeitplan, Ort, Zweck, letztem Lauf (Uhrzeit)
 *    und gruenem Erfolgs-Symbol.
 * Nutzt das gemeinsame .v-card-Fundament (frontend/style.css).
 */

interface ScheduledJobDto {
  id: string;
  title: string;
  prompt: string;
  nextRunMs: number;
  repeatEveryMs: number | null;
  status?: string;
  randomCount?: number | null;
  windowStartMin?: number | null;
  windowEndMin?: number | null;
  endOnHit: boolean;
  runs: number;
  lastRunMs?: number | null;
  lastResult: string | null;
}
interface SystemJobDto {
  label: string;
  schedule: string;
  where: string;
  info: string;
  lastRunMs?: number | null;
  ok?: boolean | null;
}

const HOUR = 3_600_000;

function fmtWhen(ms: number, withWeekday = true): string {
  try {
    return new Date(ms).toLocaleString("de-AT", {
      timeZone: "Europe/Vienna",
      ...(withWeekday ? { weekday: "short" } : {}),
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return new Date(ms).toISOString();
  }
}
function fmtTimeOnly(ms: number): string {
  try {
    return new Date(ms).toLocaleTimeString("de-AT", {
      timeZone: "Europe/Vienna",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
function hhmmOf(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
/** HH:MM (lokal = Wien) -> naechster passender Zeitpunkt als epoch ms. */
function nextEpochFor(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  const now = new Date();
  const d = new Date();
  if (m) d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1);
  return d.getTime();
}

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}
function svgIcon(path: string): SVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "18");
  svg.setAttribute("height", "18");
  svg.setAttribute("aria-hidden", "true");
  const p = document.createElementNS(ns, "path");
  p.setAttribute("fill", "currentColor");
  p.setAttribute("d", path);
  svg.appendChild(p);
  return svg;
}
const IC_BELL =
  "M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6v-5a6 6 0 0 0-5-5.91V4a1 1 0 0 0-2 0v1.09A6 6 0 0 0 6 11v5l-2 2v1h16v-1z";
const IC_CLOCK =
  "M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z";
const IC_GEAR =
  "M19.14 12.94a7.5 7.5 0 0 0 .05-1.88l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7 7 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54a7 7 0 0 0-1.62.94l-2.39-.96a.5.5 0 0 0-.6.22L2.36 8.84a.5.5 0 0 0 .12.64l2.03 1.58a7.5 7.5 0 0 0 0 1.88l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96a7 7 0 0 0 1.62.94l.36 2.54a.5.5 0 0 0 .5.42h3.84a.5.5 0 0 0 .5-.42l.36-2.54a7 7 0 0 0 1.62-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64zM12 15.5A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5z";

function minToHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function hhmmToMin(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function fmtFensterMin(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}:${String(m).padStart(2, "0")}` : `${h}:00`;
}

function scheduleChips(j: ScheduledJobDto): string[] {
  const chips: string[] = [];
  if (j.status === "paused") chips.push("pausiert");
  if (j.randomCount != null) {
    // 2026-07-22 (Martin, "warum ploetzlich einmalig?"): Zufalls-Auftraege
    // haben kein festes Intervall — das alte Etikett log.
    const fenster =
      j.windowStartMin != null && j.windowEndMin != null
        ? ` · ${fmtFensterMin(j.windowStartMin)}–${fmtFensterMin(j.windowEndMin)}`
        : "";
    chips.push(`${j.randomCount}× täglich · zufällig${fenster}`);
  } else if (!j.repeatEveryMs) {
    chips.push("einmalig");
  } else {
    const h = Math.round(j.repeatEveryMs / HOUR);
    chips.push(h === 24 ? `täglich · ${fmtTimeOnly(j.nextRunMs)}` : `alle ${h} h`);
    chips.push(j.endOnHit ? "bis Treffer" : "Dauerreport");
  }
  return chips;
}
/** Erfolgs-Status eines geplanten Auftrags: gruen wenn schon >=1x gelaufen. */
function jobStatus(j: ScheduledJobDto): "ok" | "pending" {
  return j.runs > 0 && j.lastRunMs ? "ok" : "pending";
}

export class AutomationView {
  private readonly container: HTMLElement;
  // 2026-07-05 (Martin): Tabs Eigene|System + Modal-Edit.
  private activeTab: "own" | "system" = "own";
  private jobs: ScheduledJobDto[] = [];
  private systemJobs: SystemJobDto[] = [];

  constructor(container: HTMLElement) {
    this.container = container;
    void this.refresh();
  }

  async refresh(): Promise<void> {
    const c = this.container;
    c.textContent = "";
    const wrap = el("div", "auto-wrap");
    c.appendChild(wrap);

    // 2026-07-25 (Ticket 532, gemessen): Der Kopf gehoert in denselben
    // Traeger wie der Inhalt — so macht es jede andere Seite. Vorher hing er
    // am nackten View-Container ("MASKE v6"), wodurch der Titel 16px weiter
    // links stand als auf den sechs uebrigen Seiten (380 statt 396). Eine
    // Seite, die als einzige anders ausschert, ist genau das, was Ticket 532
    // abstellen soll.
    wrap.appendChild(buildPageHeader("Automatik", () => {}));
    wrap.appendChild(
      el(
        "p",
        "auto-hint",
        "Alles, was ohne dein Zutun läuft — deine geplanten Aufträge und die System-Prozesse. Grün = zuletzt erfolgreich gelaufen.",
      ),
    );

    try {
      const r = await fetch("/api/automation/overview", {
        credentials: "same-origin",
      });
      const d = (await r.json()) as {
        jobs?: ScheduledJobDto[];
        systemJobs?: SystemJobDto[];
      };
      this.jobs = d.jobs ?? [];
      this.systemJobs = d.systemJobs ?? [];
    } catch {
      wrap.appendChild(
        el("p", "auto-hint", "Automatik-Übersicht konnte nicht geladen werden."),
      );
      return;
    }

    // Tab-Leiste Eigene | System
    const tabs = el("div", "auto-tabs");
    const mkTab = (key: "own" | "system", label: string): HTMLElement => {
      const b = el("button", "auto-tab", label) as HTMLButtonElement;
      b.type = "button";
      b.dataset.active = String(this.activeTab === key);
      b.addEventListener("click", () => {
        if (this.activeTab === key) return;
        this.activeTab = key;
        for (const t of tabs.querySelectorAll<HTMLButtonElement>(".auto-tab"))
          t.dataset.active = "false";
        b.dataset.active = "true";
        this.renderContent(content);
      });
      return b;
    };
    tabs.appendChild(mkTab("own", `Eigene (${this.jobs.length})`));
    tabs.appendChild(mkTab("system", `System (${this.systemJobs.length})`));
    wrap.appendChild(tabs);

    const content = el("div", "auto-content");
    wrap.appendChild(content);
    this.renderContent(content);
  }

  private renderContent(content: HTMLElement): void {
    content.textContent = "";
    if (this.activeTab === "own") {
      if (this.jobs.length === 0) {
        content.appendChild(
          el(
            "p",
            "auto-empty",
            "Keine aktiven Aufträge. Sag Vespera z. B. „Schau jeden Morgen um 7 in meine Mails“ oder „Erinnere mich um 15 Uhr an den Anruf“.",
          ),
        );
      }
      for (const j of this.jobs) content.appendChild(this.jobCard(j));
    } else {
      for (const s of this.systemJobs) content.appendChild(this.systemCard(s));
    }
  }

  private sectionTitle(t: string): HTMLElement {
    return el("h3", "auto-section-title", t);
  }

  private statusDot(state: "ok" | "pending" | "fail"): HTMLElement {
    const dot = el("span", `v-dot v-dot--${state}`);
    dot.setAttribute("aria-hidden", "true");
    return dot;
  }

  private jobCard(j: ScheduledJobDto): HTMLElement {
    const card = el("div", "v-card v-card--job");
    // Kopf
    const head = el("div", "v-card__head");
    head.appendChild(this.statusDot(jobStatus(j)));
    const ic = el("span", "v-card__icon");
    ic.appendChild(svgIcon(j.repeatEveryMs ? IC_CLOCK : IC_BELL));
    head.appendChild(ic);
    head.appendChild(el("div", "v-card__title", j.title));
    card.appendChild(head);

    // Chips
    const chips = el("div", "v-chips");
    for (const cText of scheduleChips(j)) chips.appendChild(el("span", "v-chip", cText));
    card.appendChild(chips);

    // Meta
    const meta = el(
      "div",
      "v-card__meta",
      // 2026-08-26 (Norm B8, "hoechstens zwei Zeitangaben-Formen und nie beide
      // in derselben Zeile"): Hier standen ZWEI volle Zeitstempel plus ein
      // Zaehler nebeneinander — "naechster Lauf: Mi., 22.07., 20:03 · zuletzt:
      // Di., 21.07., 20:03 · 34x gelaufen". Auf jeder Karte begannen dadurch
      // 21 Zeichen gleich. Was zaehlt, ist wann er das naechste Mal laeuft;
      // wann er zuletzt lief, steht im Ergebnis darunter.
      `${fmtWhen(j.nextRunMs)}  ·  ${j.runs}× gelaufen`,
    );
    card.appendChild(meta);

    if (j.lastResult) {
      const last = el("div", "v-card__result", j.lastResult.slice(0, 220));
      card.appendChild(last);
    }

    // Aktionen
    const actions = el("div", "v-card__actions");
    const edit = el("button", "v-btn v-btn--ghost", "Bearbeiten") as HTMLButtonElement;
    edit.type = "button";
    edit.addEventListener("click", () => {
      this.openEditModal(j, card);
    });
    // 2026-07-22 (Martin): Anhalten ohne Loeschen.
    const istPausiert = j.status === "paused";
    const pause = el(
      "button",
      "v-btn v-btn--ghost",
      istPausiert ? "Fortsetzen" : "Pausieren",
    ) as HTMLButtonElement;
    pause.type = "button";
    pause.addEventListener("click", () => {
      pause.disabled = true;
      void fetch("/api/scheduled-jobs/" + encodeURIComponent(j.id), {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paused: !istPausiert }),
      })
        .then(async (r) => {
          // Nur die betroffene Karte tauschen — die Seite bleibt stehen
          // (Martin, 22.07.: "nicht immer alles neu laden").
          const d = (await r.json().catch(() => null)) as {
            ok?: boolean;
            job?: ScheduledJobDto;
          } | null;
          if (d?.ok && d.job) card.replaceWith(this.jobCard(d.job));
          else if (d?.ok) void this.refresh();
          else pause.disabled = false;
        })
        .catch(() => {
          pause.disabled = false;
        });
    });
    // 2026-07-22 (Martin): "Abbrechen" war ein STILLES Loeschen ohne
    // Rueckfrage — ehrliches Etikett + Zweischritt-Bestaetigung.
    const cancel = el("button", "v-btn v-btn--danger", "Löschen") as HTMLButtonElement;
    cancel.type = "button";
    let confirmTimer: number | null = null;
    cancel.addEventListener("click", () => {
      if (cancel.dataset.confirm !== "1") {
        cancel.dataset.confirm = "1";
        cancel.textContent = "Wirklich löschen?";
        confirmTimer = window.setTimeout(() => {
          cancel.dataset.confirm = "";
          cancel.textContent = "Löschen";
        }, 4000);
        return;
      }
      if (confirmTimer) window.clearTimeout(confirmTimer);
      cancel.disabled = true;
      void fetch("/api/scheduled-jobs/" + encodeURIComponent(j.id), {
        method: "DELETE",
        credentials: "same-origin",
      })
        .then((r) => {
          if (r.ok) card.remove();
          else cancel.disabled = false;
        })
        .catch(() => {
          cancel.disabled = false;
        });
    });
    actions.appendChild(edit);
    actions.appendChild(pause);
    actions.appendChild(cancel);
    card.appendChild(actions);
    return card;
  }

  // 2026-07-05 (Martin): Bearbeiten in einem MODAL — die Liste darunter bleibt
  // unangetastet, also bleibt die Scroll-Position erhalten.
  private openEditModal(j: ScheduledJobDto, anker: HTMLElement): void {
    const overlay = el("div", "auto-modal-overlay");
    const cardEl = el("div", "auto-modal-card");
    cardEl.addEventListener("click", (e) => e.stopPropagation());
    cardEl.appendChild(el("div", "auto-modal-title", "Automatik bearbeiten"));

    const titleIn = el("input", "v-input") as HTMLInputElement;
    titleIn.type = "text";
    titleIn.value = j.title;
    titleIn.placeholder = "Titel";
    cardEl.appendChild(this.field("Titel", titleIn));

    const timeIn = el("input", "v-input v-input--time") as HTMLInputElement;
    timeIn.type = "time";
    timeIn.value = hhmmOf(j.nextRunMs);
    const timeField = this.field("Uhrzeit", timeIn);
    cardEl.appendChild(timeField);

    const sel = el("select", "v-input") as HTMLSelectElement;
    const opts: Array<[string, string]> = [
      ["0", "einmalig"],
      ["24", "täglich"],
      ["2", "alle 2 Stunden"],
      ["4", "alle 4 Stunden"],
      ["6", "alle 6 Stunden"],
      ["12", "alle 12 Stunden"],
      ["rnd", "mehrmals täglich · zufällig"],
    ];
    const curH =
      j.randomCount != null
        ? "rnd"
        : j.repeatEveryMs
          ? String(Math.round(j.repeatEveryMs / HOUR))
          : "0";
    for (const [val, label] of opts) {
      const o = document.createElement("option");
      o.value = val;
      o.textContent = label;
      if (val === curH) o.selected = true;
      sel.appendChild(o);
    }
    cardEl.appendChild(this.field("Rhythmus", sel));

    // Zufalls-Modus: Anzahl pro Tag + Tag-Fenster (Wiener Wandzeit).
    const cntIn = el("input", "v-input") as HTMLInputElement;
    cntIn.type = "number";
    cntIn.min = "1";
    cntIn.max = "24";
    cntIn.value = String(j.randomCount ?? 4);
    const winStartIn = el("input", "v-input v-input--time") as HTMLInputElement;
    winStartIn.type = "time";
    winStartIn.value = minToHHMM(j.windowStartMin ?? 480);
    const winEndIn = el("input", "v-input v-input--time") as HTMLInputElement;
    winEndIn.type = "time";
    winEndIn.value = minToHHMM(j.windowEndMin ?? 1320);
    const cntField = this.field("Wie oft pro Tag", cntIn);
    const winStartField = this.field("Frühestens", winStartIn);
    const winEndField = this.field("Spätestens", winEndIn);
    cardEl.appendChild(cntField);
    cardEl.appendChild(winStartField);
    cardEl.appendChild(winEndField);
    const syncMode = (): void => {
      const rnd = sel.value === "rnd";
      timeField.classList.toggle("is-hidden", rnd);
      cntField.classList.toggle("is-hidden", !rnd);
      winStartField.classList.toggle("is-hidden", !rnd);
      winEndField.classList.toggle("is-hidden", !rnd);
    };
    sel.addEventListener("change", syncMode);
    syncMode();

    const close = (): void => {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", close);

    const actions = el("div", "v-card__actions");
    const save = el(
      "button",
      "v-btn v-btn--primary",
      "Speichern",
    ) as HTMLButtonElement;
    save.type = "button";
    save.addEventListener("click", async () => {
      save.disabled = true;
      const body: Record<string, unknown> = {
        title: titleIn.value.trim() || j.title,
      };
      if (sel.value === "rnd") {
        body.random_count = Math.max(1, Math.min(24, Number(cntIn.value) || 4));
        let ws = hhmmToMin(winStartIn.value) ?? 480;
        let we = hhmmToMin(winEndIn.value) ?? 1320;
        if (we <= ws) we = Math.min(1439, ws + 60);
        body.window_start_min = ws;
        body.window_end_min = we;
      } else {
        const h = Number(sel.value);
        body.next_run_ms = nextEpochFor(timeIn.value || hhmmOf(j.nextRunMs));
        body.repeat_every_ms = h > 0 ? h * HOUR : null;
        // Vorher Zufalls-Job? Explizit zuruecksetzen.
        if (j.randomCount != null) body.random_count = null;
      }
      try {
        const r = await fetch("/api/scheduled-jobs/" + encodeURIComponent(j.id), {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const d = (await r.json().catch(() => null)) as {
          ok?: boolean;
          job?: ScheduledJobDto;
        } | null;
        close();
        // Nur die Karte tauschen — Seite und Scroll bleiben stehen.
        if (d?.ok && d.job) anker.replaceWith(this.jobCard(d.job));
        else await this.refresh();
      } catch {
        close();
        await this.refresh();
      }
    });
    const back = el(
      "button",
      "v-btn v-btn--ghost",
      "Abbrechen",
    ) as HTMLButtonElement;
    back.type = "button";
    back.addEventListener("click", close);
    actions.appendChild(save);
    actions.appendChild(back);
    cardEl.appendChild(actions);

    overlay.appendChild(cardEl);
    document.body.appendChild(overlay);
    titleIn.focus();
  }

  private field(label: string, input: HTMLElement): HTMLElement {
    const f = el("label", "v-field");
    f.appendChild(el("span", "v-field__label", label));
    f.appendChild(input);
    return f;
  }

  private systemCard(s: SystemJobDto): HTMLElement {
    const card = el("div", "v-card v-card--system");
    const head = el("div", "v-card__head");
    const state: "ok" | "pending" | "fail" =
      s.ok === true ? "ok" : s.ok === false ? "fail" : "pending";
    head.appendChild(this.statusDot(state));
    const ic = el("span", "v-card__icon v-card__icon--muted");
    ic.appendChild(svgIcon(IC_GEAR));
    head.appendChild(ic);
    head.appendChild(el("div", "v-card__title", s.label));
    card.appendChild(head);

    const chips = el("div", "v-chips");
    chips.appendChild(el("span", "v-chip", s.schedule));
    chips.appendChild(el("span", "v-chip v-chip--soft", s.where));
    card.appendChild(chips);

    const lastTxt = s.lastRunMs
      ? `zuletzt: ${fmtWhen(s.lastRunMs, false)}${s.ok === false ? "  ·  Fehler" : "  ✓"}`
      : "noch nicht gelaufen";
    card.appendChild(el("div", "v-card__meta", lastTxt));
    card.appendChild(el("div", "v-card__result v-card__result--plain", s.info));
    return card;
  }
}
